import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import clsx from 'clsx'
import { useSelector } from 'react-redux'
import Image from 'next/image'
import sortBy from 'lodash/sortBy'
import {
  API_GET_MARKET_OPTIONS,
  API_GET_SPREADS_MARKET,
  WEBSITE,
} from 'src/consts'
import { InnerLoader } from 'src/components/InnerLoader'
import { formatSpread2String } from 'src/utils/number/currency'
import { getCoinsDict } from 'src/redux/selectors'
import SelectComponent from './components/Select'
import styles from './styles.module.scss'
import { intl } from 'src/i18n'

const PRIORITIES = ['USDT', 'BTC', 'ETH']

const prepareOptions = (options, coinsDict) =>
  options.reduce((acc, next) => {
    const coinInDict = coinsDict[next.slug]
    if (!coinInDict) return acc

    acc[next.slug] = {
      code: next.code,
      name: coinInDict.name,
      slug: next.slug,
    }
    return acc
  }, {})

const sortOptions = options => {
  return sortBy(
    options,
    PRIORITIES.map(p => v => v.code !== p)
  )
}

const calculateSpreadWidths = orderbooks => {
  let maxSpread = 0

  for (let i = 0; i < orderbooks.length; i++) {
    if (maxSpread < orderbooks[i].spread) {
      maxSpread = orderbooks[i].spread
    }
  }

  return orderbooks.map(o => ({
    ...o,
    spreadWidth: `${(o.spread / maxSpread) * 100}%`,
  }))
}

const calculateLiquidityWidths = orderbooks => {
  let maxLiquidity = 0

  for (let i = 0; i < orderbooks.length; i++) {
    if (!maxLiquidity || maxLiquidity < orderbooks[i].liquidity) {
      maxLiquidity = orderbooks[i].liquidity
    }
  }

  return orderbooks.map(o => ({
    ...o,
    liquidityWidth: `${(o.liquidity / maxLiquidity) * 100}%`,
  }))
}

export const LiquidityComparison = ({ coin }) => {
  const coinsDict = useSelector(getCoinsDict)

  const [baseOptionsDict, setBaseOptionsDict] = useState([])
  const [quoteOptionsDict, setQuoteOptionsDict] = useState([])
  const [base, setBase] = useState('')
  const [quote, setQuote] = useState('')
  const [type, setType] = useState('')
  const [pair, setPair] = useState('')
  const [spreads, setSpreads] = useState('')
  const [liquidities, setLiquidities] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const getMarketOptions = async slug => {
    if (!slug) {
      return { bases: [], quotes: [] }
    }
    const { data } = await axios.get(`${API_GET_MARKET_OPTIONS}?slug=${slug}`)
    const { base, quote } = data
    return { bases: base || [], quotes: quote || [] }
  }

  const getMarkets = useCallback(
    async (item = coin, type = 'all') => {
      const response = await getMarketOptions(item.slug)
      const baseOptionsDict = prepareOptions(response.bases, coinsDict)
      const quoteOptionsDict = prepareOptions(response.quotes, coinsDict)

      if (type === 'all') {
        setBase(item)

        let priorityQuote = response.quotes.find(c => c.code == 'USDT')
        if (!priorityQuote) {
          priorityQuote = response.quotes.find(c => c.code == 'BTC')
        }
        if (!priorityQuote) {
          priorityQuote = response.quotes[0]
        }

        setQuote(priorityQuote)
        setPair(`${item?.code}${priorityQuote?.code}`)
      }

      if (type === 'all' || type === 'base') {
        setQuoteOptionsDict(quoteOptionsDict)
      }

      if (type === 'all' || type === 'quote') {
        setBaseOptionsDict(baseOptionsDict)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [coinsDict]
  )

  useEffect(() => {
    getMarkets()
  }, [getMarkets])

  useEffect(() => {
    const getData = async () => {
      setIsLoading(true)
      try {
        const { data } = await axios.get(`${API_GET_SPREADS_MARKET}${pair}`)

        let orderbookSpreads = []
        let orderbookLiquidities = []

        for (const exchange in data) {
          const exchangeLink = exchange === 'huobi' ? 'huobi-global' : exchange

          orderbookSpreads.push({
            exchange: exchange,
            exchangeLink: exchangeLink,
            spread: data[exchange].spread,
            stringifiedSpread: formatSpread2String(data[exchange].spread),
          })

          orderbookLiquidities.push({
            exchange: exchange,
            exchangeLink: exchangeLink,
            liquidity: data[exchange].liquidity,
            stringifiedLiquidity: formatSpread2String(
              data[exchange].liquidity,
              '0,0'
            ),
          })
        }

        orderbookSpreads.sort((a, b) => a.spread - b.spread)
        orderbookLiquidities.sort((a, b) => b.liquidity - a.liquidity)

        orderbookSpreads = calculateSpreadWidths(orderbookSpreads)
        orderbookLiquidities = calculateLiquidityWidths(orderbookLiquidities)

        setSpreads(orderbookSpreads)
        setLiquidities(orderbookLiquidities)

        if (type === 'base') {
          getMarkets(base, 'base')
        } else if (type === 'quote') {
          getMarkets(quote, 'quote')
        }
        setIsLoading(false)
      } catch (e) {
        setIsLoading(false)
        setSpreads([])
        setLiquidities([])
      }
    }
    pair && getData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pair, type, getMarkets])

  const handleChange = (value, type) => {
    let newPair

    if (type === 'base') {
      setBase(value)
      newPair = `${value.code}${quote.code}`
    } else {
      setQuote(value)
      newPair = `${base.code}${value.code}`
    }
    setType(type)
    setPair(newPair)
  }

  const baseOptionsArr = Object.values(baseOptionsDict)
  const quoteOptionsArr = Object.values(quoteOptionsDict)
  const baseOptions = sortOptions(baseOptionsArr)
  const quoteOptions = sortOptions(quoteOptionsArr)

  const notPairs = baseOptionsArr.length === 0 || quoteOptionsArr.length === 0

  return (
    <div className={styles.wrapper}>
      {isLoading && <InnerLoader className={styles.loader} />}
      <div className={styles.header}>
        <div className={styles.market}>
          <SelectComponent
            options={baseOptions}
            value={base}
            action={value => handleChange(value, 'base')}
            instanceId='liq-base'
          />
          <span className={styles.inputsDelimeter}>/</span>
          <SelectComponent
            options={quoteOptions}
            value={quote}
            action={value => handleChange(value, 'quote')}
            instanceId='liq-quote'
          />
        </div>
        <div className={clsx(styles.poweredBy, { [styles.upper]: true })}>
          {'Powered by\xa0\xa0'}
          <Image
            src='/assets/logo_gray.svg'
            alt='Cryptocurrency prices in USD, market cap, volume'
            width='100'
            height='14'
          />
        </div>
      </div>
      <div className={clsx(styles.dataBox, { [styles.notPairs]: notPairs })}>
        {!notPairs ? (
          <>
            <div className={styles.part}>
              <div className={styles.columns}>
                <table className={styles.widgetTable}>
                  <tbody>
                    <tr>
                      <td
                        className={clsx(styles.cell, {
                          [styles.exchange]: true,
                        })}
                      >
                        {intl.t('components.liquidityComparison.exchange')}
                      </td>
                      <td className={styles.cell}>
                        {intl.t('components.liquidityComparison.spread')}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className={styles.values}>
                {spreads.length === 0 && (
                  <div>{intl.t('components.liquidityComparison.noSpread')}</div>
                )}
                {spreads.length > 0 && (
                  <table className={styles.widgetTable}>
                    <tbody>
                      {spreads.map(el => (
                        <tr key={el.exchange}>
                          <td
                            className={clsx(styles.cell, {
                              [styles.exchange]: true,
                            })}
                          >
                            <a
                              className={styles.exchangeRel}
                              href={`${WEBSITE}/exchange/${el.exchangeLink}`}
                              target='_blank'
                              rel='noopener noreferrer'
                            >
                              {el.exchange.replace(/-Global/gi, '')}
                            </a>
                          </td>
                          <td className={styles.cell}>
                            <div
                              className={styles.box}
                              style={{ width: el.spreadWidth }}
                            ></div>
                            <span>{el.stringifiedSpread}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            <div className={styles.part}>
              <div className={styles.columns}>
                <table
                  className={clsx(styles.widgetTable, {
                    [styles.liquidity]: true,
                  })}
                >
                  <tbody>
                    <tr>
                      <td className={styles.cell}>
                        {intl.t('components.liquidityComparison.liquidity')}
                      </td>
                      <td
                        className={clsx(styles.cell, {
                          [styles.exchange]: true,
                        })}
                      >
                        {intl.t('components.liquidityComparison.exchange')}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className={styles.values}>
                {liquidities.length === 0 && (
                  <div>{intl.t('components.liquidityComparison.noLiquid')}</div>
                )}
                {liquidities.length > 0 && (
                  <table
                    className={clsx(styles.widgetTable, {
                      [styles.liquidity]: true,
                    })}
                  >
                    <tbody>
                      {liquidities.map(el => (
                        <tr key={el.exchange}>
                          <td className={styles.cell}>
                            <div
                              className={styles.box}
                              style={{ width: el.liquidityWidth }}
                            ></div>
                            <span>{el.stringifiedLiquidity}</span>
                          </td>
                          <td
                            className={clsx(styles.cell, {
                              [styles.exchange]: true,
                            })}
                          >
                            <a
                              className={styles.exchangeRel}
                              href={`${WEBSITE}/exchange/${el.exchangeLink}`}
                              target='_blank'
                              rel='noopener noreferrer'
                            >
                              {el.exchange.replace(/-Global/gi, '')}
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className={styles.notPairBlock}>
            Sorry, no liquidity for this pair
          </div>
        )}
      </div>
    </div>
  )
}

export default LiquidityComparison
